import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import './RecentActivityTicker.css';

interface Activity {
  id: string;
  type: 'deposit' | 'invite' | 'withdraw' | 'staking' | 'vip' | 'game_win' | 'rank_up' | 'leader_status' | 'burn' | 'coupon' | 'referral_achievement';
  user?: string;
  amount?: number;
  invited?: string;
  vipLevel?: string;
  rankName?: string;
  leaderStatus?: string;
  multiplier?: number;
  referralCount?: number;
  time: string;
  icon: string;
}

const RecentActivityTicker: React.FC = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<Activity[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const positionRef = useRef(0);

  // Загрузка реальных данных активностей
  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities/recent?limit=10');
      if (response.data.success && response.data.data) {
        const fetchedActivities = response.data.data;
        // Дублируем активности 2 раза для бесшовной JavaScript прокрутки
        setActivities([...fetchedActivities, ...fetchedActivities]);
      }
    } catch (error) {
      console.error('Ошибка загрузки активностей:', error);
      setActivities([]);
    }
  };

  // JavaScript анимация для iOS Safari
  useEffect(() => {
    const animate = () => {
      if (contentRef.current) {
        // Скорость прокрутки: 30px в секунду = 0.5px за 1 кадр при 60fps
        positionRef.current -= 0.5;

        const contentWidth = contentRef.current.scrollWidth / 2;

        // Когда прокрутили половину (одну копию), возвращаемся в начало
        if (Math.abs(positionRef.current) >= contentWidth) {
          positionRef.current = 0;
        }

        contentRef.current.style.transform = `translateX(${positionRef.current}px)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [activities]);

  useEffect(() => {
    // Загружаем активности при монтировании
    fetchActivities();

    // Обновляем каждые 15 секунд
    const intervalId = setInterval(fetchActivities, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const getActivityText = (activity: Activity) => {
    switch (activity.type) {
      case 'deposit':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.deposit')}{' '}
            <span className="ticker-amount positive">+{activity.amount?.toLocaleString()} SPERM</span>
          </>
        );
      case 'withdraw':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.withdraw')}{' '}
            <span className="ticker-amount negative">{activity.amount?.toLocaleString()} SPERM</span>
          </>
        );
      case 'invite':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.invite')} <strong>{activity.invited}</strong>
          </>
        );
      case 'staking':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.staking')}{' '}
            <span className="ticker-amount positive">{activity.amount?.toLocaleString()} SPERM</span>
          </>
        );
      case 'vip':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.vip')}{' '}
            <span className="ticker-level">{activity.vipLevel}</span>
          </>
        );
      case 'game_win':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.gameWin')}{' '}
            <span className="ticker-amount positive">{activity.multiplier}%</span>
          </>
        );
      case 'rank_up':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.rankUp')}{' '}
            <span className="ticker-level">{activity.rankName}</span>
          </>
        );
      case 'leader_status':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.leaderStatus')}{' '}
            <span className="ticker-level">{activity.leaderStatus}</span>
          </>
        );
      case 'burn':
        return (
          <>
            {t('activityTicker.burn')}{' '}
            <span className="ticker-amount negative">{activity.amount?.toLocaleString()} SPERM</span>
          </>
        );
      case 'coupon':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.coupon')}{' '}
            <span className="ticker-amount positive">x{activity.multiplier}</span>
          </>
        );
      case 'referral_achievement':
        return (
          <>
            <strong>{activity.user}</strong> {t('activityTicker.referralAchievement')}{' '}
            <span className="ticker-level">{activity.referralCount} {t('activityTicker.referrals')}</span>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="activity-ticker">
      {/* Градиенты для плавного исчезновения по краям */}
      <div className="ticker-gradient ticker-gradient-left" />
      <div className="ticker-gradient ticker-gradient-right" />

      {/* Индикатор активности */}
      <div className="ticker-indicator">
        <span className="ticker-indicator-dot"></span>
        <span className="ticker-indicator-text">LIVE</span>
      </div>

      {/* Бегущая строка - анимация через JavaScript для iOS */}
      <div className="ticker-content" ref={contentRef}>
        {activities.map((activity, index) => (
          <div key={`${activity.id}-${index}`} className="ticker-item">
            <span className="ticker-icon">{activity.icon}</span>
            <span className="ticker-text">{getActivityText(activity)}</span>
            <span className="ticker-time">{activity.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentActivityTicker;